import { Helmet } from 'react-helmet-async';

const SITE = 'MalwareAnalyzer by Cyble';
const ORIGIN = 'https://malwareanalyzer.com';

export interface SeoProps {
  /** Page title; the site name is appended automatically unless `bareTitle` is set. */
  title: string;
  description?: string;
  /** Absolute or root-relative canonical path (e.g. `/report/<hash>`). */
  canonicalPath?: string;
  /** Absolute OG image URL; defaults to the brand card. */
  image?: string;
  /** `article` for report/scan pages, else `website`. */
  type?: 'website' | 'article';
  /** Discourage indexing (auth/dashboard/transient result pages). */
  noindex?: boolean;
  bareTitle?: boolean;
}

/**
 * Per-route head management for the SPA. This is the belt-and-suspenders layer for JS-rendering
 * crawlers (Googlebot) and human UX (browser tab titles, in-app social shares): non-JS bots and
 * social scrapers are separately routed to the server-rendered /seo/* pages at the edge. Kept
 * intentionally small; page-specific structured data lives in the SSR layer.
 */
export function Seo({ title, description, canonicalPath, image, type = 'website', noindex, bareTitle }: SeoProps) {
  const fullTitle = bareTitle ? title : `${title} - ${SITE}`;
  const canonical = canonicalPath
    ? canonicalPath.startsWith('http')
      ? canonicalPath
      : `${ORIGIN}${canonicalPath}`
    : undefined;
  const ogImage = image ?? `${ORIGIN}/v1/public/og.png`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description ? <meta name="description" content={description} /> : null}
      {noindex ? <meta name="robots" content="noindex,nofollow" /> : <meta name="robots" content="index,follow,max-image-preview:large" />}
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      <meta property="og:site_name" content={SITE} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description ? <meta property="og:description" content={description} /> : null}
      {canonical ? <meta property="og:url" content={canonical} /> : null}
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description ? <meta name="twitter:description" content={description} /> : null}
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
